import { useState, useEffect } from 'react'
import { PublicGalleryItem } from '../utils/publicCacheService'
import { simpleShopifyService } from '../utils/shopifyServiceSimple'
import SEO from '../components/SEO'

// Use public cache interface
type GalleryItem = PublicGalleryItem

function Shopify() {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const itemsPerPage = 24 // Show 24 items per page

  useEffect(() => {
    const loadShopifyProducts = async () => {
      setIsLoading(true)
      console.log('Shopify: Loading Shopify products...')

      try {
        // Load Shopify products if enabled
        if (simpleShopifyService.isConfigured()) {
          const shopifyProducts = await simpleShopifyService.getProducts(3000) // Load all products (you have ~2,780)

          // Convert to gallery items
          const shopifyGalleryItems = shopifyProducts.map(product =>
            simpleShopifyService.convertToGalleryItem(product)
          )

          console.log('Shopify: Loaded', shopifyGalleryItems.length, 'Shopify products')
          setGalleryItems(shopifyGalleryItems)
        } else {
          console.warn('Shopify: Service not configured')
          setGalleryItems([])
        }
      } catch (error) {
        console.error('Shopify: Error loading Shopify products:', error)
        setGalleryItems([])
      } finally {
        setIsLoading(false)
      }
    }

    // Initial load
    loadShopifyProducts()
  }, [])

  // Get unique categories
  const categories = ['All', ...new Set((Array.isArray(galleryItems) ? galleryItems : []).map(item => item.category || 'Uncategorized'))]

  // Filter gallery items
  const filteredItems = (Array.isArray(galleryItems) ? galleryItems : []).filter(item => {
    const categoryMatch = selectedCategory === 'All' || item.category === selectedCategory
    const searchMatch = searchTerm === '' ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.artist.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    return categoryMatch && searchMatch
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = filteredItems.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCategory, searchTerm])

  const getShopifyImage = (item: GalleryItem) => {
    // For Shopify products, use the direct image URL from shopifyData
    if (item.isShopifyProduct && item.shopifyData?.images && item.shopifyData.images.length > 0) {
      let imageUrl = item.shopifyData.images[0].src

      // Use Shopify's width parameter for optimal image size
      // For 250px container, use 500px width (2x for retina displays)
      if (imageUrl.includes('?')) {
        imageUrl += '&width=500'
      } else {
        imageUrl += '?width=500'
      }

      return imageUrl
    }
    return null
  }

  const truncateDescription = (description: string, maxLength: number = 120) => {
    if (description.length <= maxLength) return description
    return description.substring(0, maxLength).trim() + '...'
  }

  return (
    <div className="py-5">
      <SEO
        title="Shop AI-Generated Art - Prints & Digital Art"
        description="Browse and purchase unique AI-generated artwork at The Salty Mermaid. Explore over 2,700 stunning digital art prints available on Shopify. Find the perfect piece for your space."
        canonicalUrl="https://saltymermaidart.com/shopify"
      />
      <div className="container">
        <h1 className="display-4 fw-bold text-center mb-4">AI-Generated Art Gallery</h1>
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  Explore our unique collection of AI-generated artwork featuring coastal themes, abstract designs, and creative digital art.
                  Each piece is an original creation powered by artificial intelligence technology.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="row mb-5 justify-content-center">
          <div className="col-md-10">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Search Products:</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                      placeholder="Search by title, artist, or tags..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Category:</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="form-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {!isLoading && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
                  {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                </p>
                {filteredItems.length > itemsPerPage && (
                  <small className="text-muted">
                    {itemsPerPage} items per page
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading products...</span>
              </div>
              <p className="mt-3 text-muted">Loading Shopify products...</p>
            </div>
          </div>
        )}

        {/* Gallery Grid */}
        {!isLoading && (
          <div className="row g-4">
            {currentItems.length > 0 ? (
              currentItems.map((item) => {
                const imageUrl = getShopifyImage(item)

                return (
                  <div key={item.id} className="col-lg-4 col-md-6">
                    <div className="card h-100 shadow-sm" style={{minHeight: '500px'}}>
                      {/* Product Image */}
                      <div
                        className="card-img-top bg-light d-flex align-items-center justify-content-center"
                        style={{
                          height: '250px',
                          overflow: 'hidden',
                          padding: '8px'
                        }}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.title}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              imageRendering: 'auto'
                            }}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                              e.currentTarget.nextElementSibling?.classList.remove('d-none')
                            }}
                          />
                        ) : (
                          <div className="display-1">🛍️</div>
                        )}
                        {imageUrl && (
                          <div className="display-1 d-none">🛍️</div>
                        )}
                      </div>

                      <div className="card-body d-flex flex-column">
                        {/* Product Header */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h5 className="card-title text-primary fw-bold">{item.title}</h5>
                              <h6 className="card-subtitle text-muted">by {item.artist}</h6>
                            </div>
                            <span className="badge bg-primary">🛍️ Shopify</span>
                          </div>
                        </div>

                        <p className="card-text text-muted" style={{
                          minHeight: '60px',
                          maxHeight: '60px',
                          overflow: 'hidden',
                          lineHeight: '1.4'
                        }}>
                          {truncateDescription(item.description)}
                        </p>

                        {/* Product Details */}
                        <div className="mb-3">
                          <div className="row g-1 small">
                            <div className="col-12">
                              <strong>Medium:</strong> {item.medium}
                            </div>
                            {item.dimensions && (
                              <div className="col-12">
                                <strong>Size:</strong> {item.dimensions}
                              </div>
                            )}
                            {item.year && (
                              <div className="col-12">
                                <strong>Year:</strong> {item.year}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Tags */}
                        {item.tags && (
                          <div className="mb-3">
                            {item.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="badge bg-light text-dark me-1">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Pricing and Purchase */}
                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              {item.price && parseFloat(item.price.toString()) > 0 ? (
                                <>
                                  <span className="h5 text-success fw-bold">${parseFloat(item.price.toString()).toFixed(2)}</span>
                                  <div className="small text-success">🟢 Available for purchase</div>
                                </>
                              ) : (
                                <div className="small text-muted">Contact for pricing</div>
                              )}
                            </div>

                            {item.shopifyData && (
                              <a
                                href={simpleShopifyService.getProductUrl(item.shopifyData.handle)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary"
                              >
                                🛍️ Buy on Shopify
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="col-12">
                <div className="alert alert-info text-center">
                  <h5>No products match your search</h5>
                  <p className="mb-0">Try adjusting your search term or filters.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pagination Controls */}
        {!isLoading && totalPages > 1 && (
          <div className="row mt-5">
            <div className="col-12">
              <nav aria-label="Product pagination">
                <ul className="pagination justify-content-center">
                  {/* Previous Button */}
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <span aria-hidden="true">&laquo;</span> Previous
                    </button>
                  </li>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 10) {
                      pageNum = i + 1;
                    } else if (currentPage <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage > totalPages - 5) {
                      pageNum = totalPages - 9 + i;
                    } else {
                      pageNum = currentPage - 5 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    )
                  })}

                  {/* Next Button */}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next <span aria-hidden="true">&raquo;</span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* No items message */}
        {!isLoading && galleryItems.length === 0 && (
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="display-1 mb-4">🛍️</div>
              <h3 className="text-white">Store Coming Soon!</h3>
              <p className="text-white">We're setting up our Shopify store. Check back soon for our full product catalog!</p>
            </div>
          </div>
        )}

        {/* Store Info Section */}
        <div className="row mt-5 pt-5 border-top">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h3 className="mb-3">AI-Generated Art Collection</h3>
                <p className="text-dark mb-0">
                  Discover unique AI-created artwork that blends technology with artistic vision.
                  All pieces are securely hosted on our Shopify store for easy and safe checkout.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Shopify
